import {Button, Checkbox, Col, Collapse, Divider, Input, Radio, Row, Space} from "antd";
import React, {useEffect, useMemo, useState} from "react";
import {IssueFilterDrawer} from "@components/issues-filter-drawer";
import {fixFilterObjectEmptyArrays, IssueFilterDrawerProps} from "@components/issues-filter-drawer/IssueFilterDrawer";
import {isEqual} from "underscore";
import {IssueFilters, Publisher, SeriesType, useGetIssueErasQuery} from "@mwServices/Issue";
import {FilterIcon} from "@components/icons/Icons";
import {formatNumber, formatTitleCase} from "@lib/format";
import {SCREEN, useWindowSize} from "@components/window-size-provider";
import {transformEra} from "@lib/utils";

const { Panel } = Collapse;

interface SearchIssuesFiltersDrawerProps extends IssueFilterDrawerProps {
    publishers: Array<Publisher>;
    seriesTypes: Array<SeriesType>;
    isHotCount: number | null;
    isKeyCount: number | null;
    isRareCount: number | null;
}

const SearchIssuesFiltersContent = ({ filters, setFilters, publishers, seriesTypes, isHotCount, isKeyCount, isRareCount }: SearchIssuesFiltersDrawerProps) => {
    const erasQuery = useGetIssueErasQuery(null);
    const eras = useMemo(() => {
        if(erasQuery.data) return erasQuery.data.eras;
        return [];
    }, [erasQuery]);

    return (
        <>
            <Row>
                <Col span={24}>
                    <div className="font-18 font-bold" >Sort by:</div>
                    <br/>
                    <Radio.Group
                        className="pad-left-10"
                        onChange={(event) => setFilters({...filters, sort: event.target.value})}
                        value={filters.sort}
                    >
                        <Space direction="vertical">
                            <Radio value={0} >Most Relevant</Radio>
                            <Radio value={1} >Title A-Z</Radio>
                            <Radio value={2} >Title Z-A</Radio>
                            <Radio value={3} >Series Year (newest first)</Radio>
                            <Radio value={4} >Series Year (oldest first)</Radio>
                            <Radio value={5} >Most Popular</Radio>
                            <Radio value={6} >Least Popular</Radio>
                            <Radio value={7} >Publish Date (Newest First)</Radio>
                            <Radio value={8} >Publish Date (Oldest First)</Radio>
                        </Space>
                    </Radio.Group>
                </Col>
            </Row>

            <Divider />

            <Row>
                <Col span={24}>
                    <div className="font-18 font-bold">Filter by:</div>
                    <Collapse className="filtersAccordion" ghost defaultActiveKey={[1,2,3]} expandIconPosition={"end"}>
                        <Panel header="Era" key={1}>
                            <Space direction="vertical">
                                <Checkbox
                                    checked={!filters.multipleDateRanges}
                                    onChange={(event) => {
                                        setFilters({...filters, multipleDateRanges: event.target.checked ? undefined : []});
                                    }}
                                >All</Checkbox>
                                {eras.map((era, i) => (
                                    <Checkbox
                                        key={i}
                                        checked={!filters.multipleDateRanges || filters.multipleDateRanges.findIndex(dr => !!dr.id && dr.id == era.id) !== -1}
                                        onChange={(event) => {
                                            if(!filters.multipleDateRanges && !event.target.checked){
                                                setFilters({...filters, multipleDateRanges: eras.map(transformEra).filter(item => item.id !== era.id)});
                                            } else if(filters.multipleDateRanges) {
                                                let filterDateRanges: any = filters.multipleDateRanges ? [...filters.multipleDateRanges] : [];
                                                if (event.target.checked) {
                                                    filterDateRanges = [...filterDateRanges, transformEra(era)];
                                                } else {
                                                    filterDateRanges = !!filters.multipleDateRanges ? filters.multipleDateRanges.filter(item => !!item.id && item.id !== era.id) : [];
                                                }
                                                if (filterDateRanges.length === eras.length) filterDateRanges = undefined;
                                                setFilters({...filters, multipleDateRanges: filterDateRanges});
                                            }
                                        }}
                                    >
                                        {formatTitleCase(era.name, 'Unknown Era')} ({era.count})
                                    </Checkbox>
                                ))}
                            </Space>
                        </Panel>
                        <Panel header="Publisher" key={2}>
                            <Space direction="vertical">
                                <Checkbox
                                    checked={!filters.publishers}
                                    onChange={(event) => {
                                        setFilters({...filters, publishers: event.target.checked ? undefined : []})
                                    }}
                                >All</Checkbox>
                                {publishers.map((publisher,i) => (
                                    <Checkbox
                                        key={i}
                                        checked={!filters.publishers || filters.publishers.indexOf(publisher.id) !== -1}
                                        onChange={(event) => {
                                            if(!filters.publishers && !event.target.checked) {
                                                setFilters({...filters, publishers: publishers.map(item => item.id).filter(item => item !== publisher.id)});
                                            }else if(filters.publishers) {
                                                let filtered: any = filters.publishers ? [...filters.publishers] : [];
                                                if (event.target.checked) {
                                                    filtered = [...filtered, publisher.id]
                                                } else {
                                                    filtered = !!filters.publishers ? filters.publishers.filter(item => item !== publisher.id) : [];
                                                }
                                                if (filtered.length === publishers.length) filtered = undefined;
                                                setFilters({...filters, publishers: filtered});
                                            }
                                        }}
                                    >
                                        {formatTitleCase(publisher.title, 'Unknown Publisher')} ({formatNumber(publisher.count)})
                                    </Checkbox>
                                ))}

                            </Space>
                        </Panel>
                        <Panel header="Series Type" key={3}>
                            <Space direction="vertical">
                                <Checkbox
                                    checked={!filters.formats}
                                    onChange={(event) => {
                                        setFilters({...filters, formats: event.target.checked ? undefined : []})
                                    }}
                                >All</Checkbox>
                                {seriesTypes && seriesTypes.map((seriesType, i) =>  (
                                    <Checkbox
                                        key={i}
                                        checked={!filters.formats || filters.formats.indexOf(seriesType.title) !== -1}
                                        onChange={(event) => {
                                            if(!filters.formats && !event.target.checked) {
                                                setFilters({...filters, formats: seriesTypes.map(item => item.title).filter(item => item !== seriesType.title)});
                                            }else if(filters.formats) {
                                                let filterFormats: any = filters.formats ? [...filters.formats] : [];
                                                if(event.target.checked){
                                                    filterFormats = [...filterFormats, seriesType.title]
                                                }else{
                                                    filterFormats = !!filters.formats ? filters.formats.filter(item => item !== seriesType.title) : [];
                                                }
                                                if(filterFormats.length === seriesTypes.length) filterFormats = undefined;
                                                setFilters({...filters, formats:filterFormats});
                                            }
                                        }}
                                    >
                                        {formatTitleCase(seriesType.title, 'Unknown Series Type')} ({seriesType.count})
                                    </Checkbox>
                                ))}
                            </Space>
                        </Panel>
                        <Panel header="Hot / Key / Rare" key={4}>
                            <Space direction="vertical">
                                <Checkbox
                                    checked={filters.isHot}
                                    onChange={() => setFilters({...filters, isHot: !filters.isHot})}
                                >Hot{isHotCount != null && ` (${isHotCount})`}</Checkbox>
                                <Checkbox
                                    checked={filters.isKey}
                                    onChange={() => setFilters({...filters, isKey: !filters.isKey})}
                                >Key{isKeyCount != null && ` (${isKeyCount})`}</Checkbox>
                                <Checkbox
                                    checked={filters.isRare}
                                    onChange={() => setFilters({...filters, isRare: !filters.isRare})}
                                >Rare{isRareCount != null && ` (${isRareCount})`}</Checkbox>
                            </Space>
                        </Panel>
                        <Panel header="Barcode" key={5}>
                            <Space direction="vertical">
                                <Input
                                    name="barcode"
                                    type="text"
                                    placeholder="barcode..."
                                    value={filters.barcode}
                                    onChange={(event) => { setFilters({...filters, barcode: event.target.value}) }}
                                />
                            </Space>
                        </Panel>
                    </Collapse>

                </Col>
            </Row>
        </>
    );
};

const SearchIssuesFiltersDrawer = ({ filters, setFilters, publishers, seriesTypes, isHotCount, isKeyCount, isRareCount }) => {
    const windowDimensions = useWindowSize();
    const [showFilters, setShowFilters] = useState<boolean>(false);
    const [newFilters, setNewFilters] = useState<IssueFilters>(filters);

    useEffect(() => {
        if(!isEqual(newFilters, filters)) {
            setNewFilters(filters);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    const onClose = () => {
        setNewFilters(filters);
        setShowFilters(false);
    };

    return (
        <>
            <Button className="greyButton" size={"large"} onClick={() => setShowFilters(true)} >
                {windowDimensions.up(SCREEN.SM) && (<span className="filterButtonText">Filter / Sort</span>)}
                <FilterIcon className="font-12" />
            </Button>

            <IssueFilterDrawer visible={showFilters} onClose={onClose} onSearch={() => {
                setShowFilters(false);

                const fixedFilters = fixFilterObjectEmptyArrays(
                    newFilters,
                    filters,
                    ['publishers', 'multipleDateRanges', 'formats']);

                if(!isEqual(fixedFilters, filters)) {
                    setFilters(fixedFilters);
                }else if(!isEqual(fixedFilters, newFilters)) {
                    setNewFilters(fixedFilters);
                }
            }}>
                <SearchIssuesFiltersContent filters={newFilters} setFilters={setNewFilters} publishers={publishers} seriesTypes={seriesTypes} isHotCount={isHotCount} isKeyCount={isKeyCount} isRareCount={isRareCount}/>
            </IssueFilterDrawer>
        </>
    )
};

export default SearchIssuesFiltersDrawer;